import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Package,
  User,
  Mail,
  Phone,
  MapPin,
  ShoppingCart,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  RefreshCw,
  ArrowRight,
  Receipt,
  Building2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Order status configuration
export const ORDER_STATUSES = {
  pending: { label: "Pending", color: "bg-yellow-500/20 text-yellow-400", icon: Clock, next: "processing" },
  confirmed: { label: "Confirmed", color: "bg-yellow-500/20 text-yellow-400", icon: Clock, next: "processing" },
  processing: { label: "Processing", color: "bg-blue-500/20 text-blue-400", icon: Package, next: "shipped" },
  shipped: { label: "Shipped", color: "bg-purple-500/20 text-purple-400", icon: Truck, next: "completed" },
  completed: { label: "Completed", color: "bg-green-500/20 text-green-400", icon: CheckCircle, next: null },
  cancelled: { label: "Cancelled", color: "bg-red-500/20 text-red-400", icon: XCircle, next: null }
};

export const OrdersManagement = ({ orders, onRefresh }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [updating, setUpdating] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [statusNotes, setStatusNotes] = useState("");

  const filteredOrders = orders.filter(order => {
    if (statusFilter === "all") return true;
    return order.status === statusFilter;
  });

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setTrackingNumber(order.tracking_number || "");
    setStatusNotes("");
    setDetailsOpen(true);
  };

  const updateOrderStatus = async (newStatus) => {
    if (!selectedOrder) return;
    
    if (newStatus === "shipped" && !trackingNumber.trim()) {
      toast.error("Please enter a tracking number before marking as shipped");
      return;
    }

    setUpdating(true);
    try {
      await axios.put(`${API}/shop/orders/${selectedOrder.order_id}/status`, {
        status: newStatus,
        tracking_number: trackingNumber.trim() || null,
        notes: statusNotes.trim() || null
      });
      
      toast.success(`Order ${newStatus === "shipped" ? "marked as shipped! Email notification sent to customer." : `updated to ${newStatus}`}`);
      setDetailsOpen(false);
      onRefresh();
    } catch (err) {
      console.error("Error updating order:", err);
      toast.error(err.response?.data?.detail || "Failed to update order");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusCounts = () => {
    const counts = { all: orders.length };
    Object.keys(ORDER_STATUSES).forEach(status => {
      counts[status] = orders.filter(o => o.status === status).length;
    });
    return counts;
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="space-y-6">
      {/* Status Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={statusFilter === "all" ? "default" : "outline"}
          onClick={() => setStatusFilter("all")}
          className={statusFilter === "all" ? "bg-[#23F7DD] text-black" : "border-white/20 text-white"}
          size="sm"
        >
          All ({statusCounts.all})
        </Button>
        {Object.entries(ORDER_STATUSES).map(([key, config]) => {
          const IconComponent = config.icon;
          return (
            <Button
              key={key}
              variant={statusFilter === key ? "default" : "outline"}
              onClick={() => setStatusFilter(key)}
              className={statusFilter === key ? config.color.replace("/20", "") : "border-white/20 text-white"}
              size="sm"
            >
              <IconComponent className="w-4 h-4 mr-1" />
              {config.label} ({statusCounts[key] || 0})
            </Button>
          );
        })}
        <Button
          variant="outline"
          onClick={onRefresh}
          className="border-white/20 text-white ml-auto"
          size="sm"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Orders List */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#23F7DD]" />
            Orders
            {statusFilter !== "all" && (
              <Badge className={ORDER_STATUSES[statusFilter]?.color}>
                {ORDER_STATUSES[statusFilter]?.label}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-[#94A3B8]">
              <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No {statusFilter !== "all" ? statusFilter : ""} orders</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredOrders.map((order) => {
                const StatusIcon = ORDER_STATUSES[order.status]?.icon || Clock;
                const statusConfig = ORDER_STATUSES[order.status] || ORDER_STATUSES.pending;
                
                return (
                  <div 
                    key={order.order_id} 
                    className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-[#23F7DD]/50 cursor-pointer transition-all"
                    onClick={() => openOrderDetails(order)}
                    data-testid={`order-${order.order_id}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusConfig.color}`}>
                          <StatusIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-white font-mono font-medium">{order.order_number}</p>
                          <p className="text-sm text-[#94A3B8]">
                            {order.buyer_name || order.guest_info?.name || order.buyer_email || "Guest"}
                          </p>
                          <p className="text-xs text-[#64748B]">
                            {new Date(order.created_at).toLocaleDateString()} • {order.items?.length || 0} items
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[#23F7DD] font-bold text-lg">{order.total_amount?.toLocaleString()} NOK</p>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                        {order.tracking_number && (
                          <p className="text-xs text-[#94A3B8] mt-1">📦 {order.tracking_number}</p>
                        )}
                      </div>
                      <Eye className="w-5 h-5 text-[#64748B]" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#23F7DD]" />
              Order {selectedOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Status Progress */}
              <div className="p-4 bg-white/5 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  {["pending", "processing", "shipped", "completed"].map((status, index) => {
                    const config = ORDER_STATUSES[status];
                    const IconComponent = config.icon;
                    // Treat "confirmed" same as "pending" for progress display
                    const currentStatus = selectedOrder.status === "confirmed" ? "pending" : selectedOrder.status;
                    const isActive = currentStatus === status;
                    const isPast = ["pending", "processing", "shipped", "completed"].indexOf(currentStatus) > index;
                    
                    return (
                      <div key={status} className="flex items-center">
                        <div className={`flex flex-col items-center ${index > 0 ? "ml-2" : ""}`}>
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isActive ? config.color : isPast ? "bg-green-500/20 text-green-400" : "bg-white/10 text-[#64748B]"
                          }`}>
                            <IconComponent className="w-5 h-5" />
                          </div>
                          <span className={`text-xs mt-1 ${isActive ? "text-white font-medium" : "text-[#64748B]"}`}>
                            {config.label}
                          </span>
                        </div>
                        {index < 3 && (
                          <div className={`w-12 h-0.5 ${isPast ? "bg-green-500" : "bg-white/10"}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Customer Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-sm text-[#94A3B8] mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Customer
                  </h4>
                  <p className="text-white font-medium">
                    {selectedOrder.buyer_name || selectedOrder.guest_info?.name || "Guest"}
                  </p>
                  {(selectedOrder.buyer_email || selectedOrder.guest_info?.email) && (
                    <p className="text-sm text-[#94A3B8] flex items-center gap-1 mt-1">
                      <Mail className="w-3 h-3" />
                      {selectedOrder.buyer_email || selectedOrder.guest_info?.email}
                    </p>
                  )}
                  {selectedOrder.guest_info?.phone && (
                    <p className="text-sm text-[#94A3B8] flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {selectedOrder.guest_info.phone}
                    </p>
                  )}
                </div>
                
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-sm text-[#94A3B8] mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Shipping Address
                  </h4>
                  <p className="text-white text-sm">
                    {selectedOrder.shipping_address || selectedOrder.guest_info?.address || "Not provided"}
                  </p>
                  {selectedOrder.guest_info?.city && (
                    <p className="text-sm text-[#94A3B8]">
                      {selectedOrder.guest_info.postal_code} {selectedOrder.guest_info.city}
                    </p>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="p-4 bg-white/5 rounded-lg">
                <h4 className="text-sm text-[#94A3B8] mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" /> Items ({selectedOrder.items?.length || 0})
                </h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-2 bg-black/30 rounded hover:bg-black/50 transition-colors cursor-pointer group"
                      onClick={() => window.open(`/webshop/product/${item.product_id}`, '_blank')}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white/10 rounded flex items-center justify-center overflow-hidden">
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-5 h-5 text-[#64748B]" />
                          )}
                        </div>
                        <div>
                          <p className="text-white text-sm group-hover:text-teal-400 transition-colors flex items-center gap-2">
                            {item.name}
                            <Eye className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </p>
                          <div className="flex items-center gap-2">
                            {item.sku && (
                              <Badge variant="outline" className="text-xs border-teal-500/30 text-teal-400">
                                SKU: {item.sku}
                              </Badge>
                            )}
                            {item.size && (
                              <Badge variant="outline" className="text-xs border-white/20 text-[#94A3B8]">
                                Size: {item.size}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white">{item.quantity} × {item.price} NOK</p>
                        <p className="text-sm text-[#23F7DD]">{item.subtotal || (item.quantity * item.price)} NOK</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Order Summary with MVA */}
                <div className="border-t border-white/10 mt-3 pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#94A3B8]">Subtotal (ex. MVA)</span>
                    <span className="text-white">
                      {((selectedOrder.subtotal || selectedOrder.total_amount) / 1.25).toFixed(0)} NOK
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#94A3B8]">MVA (25%)</span>
                    <span className="text-white">
                      {(((selectedOrder.subtotal || selectedOrder.total_amount) / 1.25) * 0.25).toFixed(0)} NOK
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#94A3B8]">Subtotal (inc. MVA)</span>
                    <span className="text-white">
                      {(selectedOrder.subtotal || (selectedOrder.total_amount - (selectedOrder.shipping || 0))).toLocaleString()} NOK
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#94A3B8]">Shipping</span>
                    <span className="text-white">
                      {selectedOrder.shipping > 0 ? `${selectedOrder.shipping} NOK` : 
                       selectedOrder.total_amount > 500 ? "Free (over 500 NOK)" : "0 NOK"}
                    </span>
                  </div>
                  {selectedOrder.selected_shipping && (
                    <div className="flex justify-between text-xs">
                      <span className="text-[#64748B]">Carrier</span>
                      <span className="text-[#64748B]">
                        {selectedOrder.selected_shipping.carrier} - {selectedOrder.selected_shipping.service_name}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-white/5">
                    <span className="text-white font-medium">Total</span>
                    <span className="text-[#23F7DD] font-bold text-lg">{selectedOrder.total_amount?.toLocaleString()} NOK</span>
                  </div>
                </div>
              </div>

              {/* Tracking Number */}
              {(selectedOrder.status === "processing" || selectedOrder.status === "shipped") && (
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <h4 className="text-sm text-purple-400 mb-2 flex items-center gap-2">
                    <Truck className="w-4 h-4" /> Tracking Information
                  </h4>
                  <Input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Enter tracking number (e.g., BRING123456)"
                    className="bg-black/50 border-white/10 text-white"
                  />
                  {selectedOrder.status === "processing" && (
                    <p className="text-xs text-[#94A3B8] mt-2 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      Customer will receive an email notification with tracking details when marked as shipped
                    </p>
                  )}
                </div>
              )}

              {/* Status Notes */}
              {selectedOrder.status !== "completed" && selectedOrder.status !== "cancelled" && (
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">Notes (optional)</label>
                  <Input
                    value={statusNotes}
                    onChange={(e) => setStatusNotes(e.target.value)}
                    placeholder="Add a note for this status update..."
                    className="bg-black/50 border-white/10 text-white"
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                {(selectedOrder.status === "pending" || selectedOrder.status === "confirmed") && (
                  <Button
                    onClick={() => updateOrderStatus("processing")}
                    disabled={updating}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    {updating ? "Updating..." : "Start Processing"}
                  </Button>
                )}
                
                {selectedOrder.status === "processing" && (
                  <Button
                    onClick={() => updateOrderStatus("shipped")}
                    disabled={updating}
                    className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    {updating ? "Updating..." : "Mark as Shipped"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                
                {selectedOrder.status === "shipped" && (
                  <Button
                    onClick={() => updateOrderStatus("completed")}
                    disabled={updating}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {updating ? "Updating..." : "Mark as Completed"}
                  </Button>
                )}
                
                {selectedOrder.status !== "cancelled" && selectedOrder.status !== "completed" && (
                  <Button
                    onClick={() => updateOrderStatus("cancelled")}
                    disabled={updating}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                )}
                
                {(selectedOrder.status === "completed" || selectedOrder.status === "cancelled") && (
                  <p className="text-center text-[#94A3B8] w-full py-2">
                    This order is {selectedOrder.status}. No further actions available.
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
